import { Injectable } from '@angular/core';
import { ProfileDataService } from './profile-data.service';
import { lastValueFrom } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService {

private userPreferencesKey: string = 'userPreferences';
private readonly MAX_STATE_SIZE = 500 * 1024; // 500KB limit per state
private readonly MAX_TOTAL_STORAGE = 4 * 1024 * 1024; // 4MB total limit

constructor(private readonly profileDataService: ProfileDataService) { }

    /**
     * Compress and save grid state with fallback strategies
     */
    async saveControlState(storageKey: string, e: any) {
      if (!e) {
        this.clearControlState(storageKey);
        return;
      }

    try {
        // Clean and optimize the state before saving
        const optimizedState = this.optimizeGridState(e);
        const stateToSave = typeof optimizedState === 'string' ? optimizedState : JSON.stringify(optimizedState);

        // Check if the state is too large
        if (stateToSave.length > this.MAX_STATE_SIZE) {
          console.warn(`Grid state for ${storageKey} is too large (${stateToSave.length} bytes), applying aggressive optimization`);

          // Try more aggressive optimization
          const minimalState = this.createMinimalState(optimizedState);
          const minimalStateString = JSON.stringify(minimalState);

          if (minimalStateString.length > this.MAX_STATE_SIZE) {
            console.warn(`Even minimal state for ${storageKey} is too large, skipping save`);
            return;
          }

          this.saveToStorage(storageKey, minimalStateString);
        } else {
          this.saveToStorage(storageKey, stateToSave);
        }

      } catch (error) {
        console.error('Error processing control state for saving:', error);
        // Don't save corrupted state
      }
    }

    /**
     * Optimize grid state by removing unnecessary data and limiting array sizes
     */
    private optimizeGridState(state: any): any {
      if (typeof state === 'string') {
        try {
          state = JSON.parse(state);
        } catch {
          return state; // Return as-is if can't parse
        }
      }

      if (!state || typeof state !== 'object') {
        return state;
      }

      const optimized = { ...state };

      // Limit the number of selected rows to prevent excessive storage
      if (optimized.selectedRowKeys && Array.isArray(optimized.selectedRowKeys)) {
        if (optimized.selectedRowKeys.length > 1000) {
          console.warn(`Limiting selected rows from ${optimized.selectedRowKeys.length} to 1000`);
          optimized.selectedRowKeys = optimized.selectedRowKeys.slice(0, 1000);
        }
      }

      // Optimize column state - remove verbose properties
      if (optimized.columns && Array.isArray(optimized.columns)) {
        optimized.columns = optimized.columns.map(col => {
          if (typeof col === 'object' && col !== null) {
            // Keep only essential properties
            const essentialCol: any = {};

            // Core properties needed for state restoration
            const essentialProps = [
              'dataField', 'dataType', 'visible', 'visibleIndex', 'width',
              'sortOrder', 'sortIndex', 'groupIndex', 'filterValue', 'filterType',
              'selectedFilterOperation', 'fixed', 'fixedPosition'
            ];

            essentialProps.forEach(prop => {
              if (col[prop] !== undefined) {
                essentialCol[prop] = col[prop];
              }
            });

            return essentialCol;
          }
          return col;
        });
      }

      // Remove potentially large or unnecessary properties
      const propsToRemove = ['searchText', 'customStore', 'dataSource'];
      propsToRemove.forEach(prop => {
        if (optimized[prop]) {
          delete optimized[prop];
        }
      });

      return optimized;
    }

    /**
     * Create minimal state with only the most essential information
     */
    private createMinimalState(state: any): any {
      if (typeof state === 'string') {
        try {
          state = JSON.parse(state);
        } catch {
          return {}; // Return empty state if can't parse
        }
      }

      if (!state || typeof state !== 'object') {
        return {};
      }

      const minimal: any = {};

      // Only keep the most critical state information
      if (state.columns && Array.isArray(state.columns)) {
        minimal.columns = state.columns.map(col => {
          if (typeof col === 'object' && col !== null) {
            const minCol: any = {};

            // Only the absolute essentials for column state
            if (col.dataField) minCol.dataField = col.dataField;
            if (col.visible !== undefined) minCol.visible = col.visible;
            if (col.visibleIndex !== undefined) minCol.visibleIndex = col.visibleIndex;
            if (col.width !== undefined) minCol.width = col.width;
            if (col.sortOrder) minCol.sortOrder = col.sortOrder;
            if (col.sortIndex !== undefined) minCol.sortIndex = col.sortIndex;
            if (col.groupIndex !== undefined) minCol.groupIndex = col.groupIndex;

            return Object.keys(minCol).length > 0 ? minCol : null;
          }
          return null;
        }).filter(col => col !== null);
      }

      // Keep selection but limit it severely
      if (state.selectedRowKeys && Array.isArray(state.selectedRowKeys)) {
        minimal.selectedRowKeys = state.selectedRowKeys.slice(0, 100); // Only first 100 selections
      }

      // Keep basic filter state
      if (state.filterValue) {
        minimal.filterValue = state.filterValue;
      }

      return minimal;
    }

    /**
     * Save to storage with quota management
     */
    private saveToStorage(storageKey: string, stateString: string): void {
      try {
        // Check total storage usage before saving
        const currentUsage = this.calculateStorageUsage();
        if (currentUsage + stateString.length > this.MAX_TOTAL_STORAGE) {
          console.warn('Total storage approaching limit, cleaning up old grid states');
          this.cleanupOldGridStates();
        }

        localStorage.setItem(storageKey, stateString);

      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          console.warn('localStorage quota exceeded, attempting cleanup and retry');

          // Try cleanup and retry once
          this.cleanupOldGridStates();

          try {
            localStorage.setItem(storageKey, stateString);
          } catch (retryError) {
            console.warn('localStorage still full after cleanup, falling back to sessionStorage');

            try {
              sessionStorage.setItem(storageKey, stateString);
            } catch (sessionError) {
              console.error('sessionStorage also failed, clearing this specific state');
              // As last resort, just clear this specific key
              this.clearControlState(storageKey);
            }
          }
        } else {
          console.error('Error saving control state:', error);
        }
      }
    }

    /**
     * Calculate current localStorage usage
     */
    private calculateStorageUsage(): number {
      let total = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const value = localStorage.getItem(key);
          if (value) {
            total += key.length + value.length;
          }
        }
      }
      return total;
    }

    /**
     * Clean up old grid states to free space
     */
    private cleanupOldGridStates(): void {
      const keysToRemove: string[] = [];
      const gridStateKeys: { key: string, size: number, timestamp?: number }[] = [];

      // Find all grid-related keys
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const value = localStorage.getItem(key);
          if (value) {
            // Check if this looks like a grid state key
            if (key.includes('_Grid') || key.includes('Tasks_') || key.includes('expandedRowKey') || key.includes('selectedRowKey')) {
              gridStateKeys.push({
                key: key,
                size: key.length + value.length,
                timestamp: this.extractTimestampFromKey(key)
              });
            }
          }
        }
      }

      // Sort by timestamp (oldest first) and size (largest first)
      gridStateKeys.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return a.timestamp - b.timestamp; // Older states first
        }
        return b.size - a.size; // Larger states first if no timestamp
      });

      // Remove up to 25% of grid states, prioritizing old and large ones
      const maxToRemove = Math.max(1, Math.floor(gridStateKeys.length * 0.25));
      const keysToDelete = gridStateKeys.slice(0, maxToRemove);

      let freedSpace = 0;
      keysToDelete.forEach(({ key, size }) => {
        localStorage.removeItem(key);
        freedSpace += size;
        // console.log(`Cleaned up grid state: ${key} (${size} bytes)`);
      });

      // console.log(`Freed ${freedSpace} bytes by removing ${keysToDelete.length} grid states`);
    }

    /**
     * Try to extract timestamp from key name (basic heuristic)
     */
    private extractTimestampFromKey(key: string): number | undefined {
      // This is a simple heuristic - in practice you might want to store metadata
      // For now, we'll assume newer keys have higher hash values
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        const char = key.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash);
    }

    loadControlState(storageKey: string): string {
      // Try localStorage first, then sessionStorage
      return localStorage.getItem(storageKey) || sessionStorage.getItem(storageKey);
    }

    clearControlState(storageKey: string): void {
      localStorage.removeItem(storageKey);
      sessionStorage.removeItem(storageKey);
    }

    /**
     * Public method to manually clean up grid states
     */
    public cleanupGridStates(): void {
      this.cleanupOldGridStates();
    }

    /**
     * Get storage usage statistics
     */
    public getStorageStats(): { usage: number, maxSize: number, percentage: number, gridStateCount: number } {
      const usage = this.calculateStorageUsage();
      let gridStateCount = 0;

      for (let key in localStorage) {
        if (key.includes('_Grid') || key.includes('Tasks_') || key.includes('expandedRowKey') || key.includes('selectedRowKey')) {
          gridStateCount++;
        }
      }

      return {
        usage: usage,
        maxSize: this.MAX_TOTAL_STORAGE,
        percentage: Math.round((usage / this.MAX_TOTAL_STORAGE) * 100),
        gridStateCount: gridStateCount
      };
    }
}
